import React, { useState } from 'react';
import { CheckCircle2, MinusCircle, Send } from 'lucide-react';
import {
  getPushCampaignErrorCode,
  testSendPushCampaign,
  type PushTestSendResult,
} from '../../services/resortAdminApi';
import { getPushCampaignErrorMessage } from '../../utils/pushCampaignValidation';

const REASON_LABELS: Record<string, string> = {
  no_device: 'no registered device',
  cancelled: 'cancelled',
  account_deleted: 'account deleted',
  not_active: 'account not active',
  unregistered_token: 'the device token is dead',
  fcm_error: 'FCM rejected the message',
  max_attempts: 'too many attempts',
  lease_thrash: 'the recipient could not be locked',
};

interface TestSendPanelProps {
  campaignName: string | null;
  modified: string | null;
  canTestSend: boolean;
  isContentTested: boolean;
  onTestSent: () => void;
  onBeforeSend?: () => Promise<{ name: string; modified: string; } | null>;
}

export const TestSendPanel: React.FC<TestSendPanelProps> = props => {
  const [result, setResult] = useState<PushTestSendResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  const isDisabled = !props.canTestSend || props.campaignName == null || props.modified == null || isSending;

  const handleSend = async () => {
    if (props.campaignName == null || props.modified == null || isSending) {
      return;
    }
    setIsSending(true);
    setError(null);
    try {
      let target = { name: props.campaignName, modified: props.modified };
      if (props.onBeforeSend != null) {
        const saved = await props.onBeforeSend();
        if (saved == null) {
          setIsSending(false);
          return;
        }
        target = saved;
      }
      const response = await testSendPushCampaign(target.name, target.modified);
      setResult(response);
      props.onTestSent();
    } catch (failure) {
      setResult(null);
      setError(getPushCampaignErrorMessage(getPushCampaignErrorCode(failure), (failure as Error)?.message || 'Test send failed.'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Test send</h3>
          <p className="text-xs text-gray-500">
            The push goes only to the test addresses of this campaign, never to the campaign audience.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSend}
          disabled={isDisabled}
          className="inline-flex shrink-0 items-center rounded-md border border-transparent bg-alps-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-alps-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Send className="mr-2 h-4 w-4" />
          {isSending ? 'Sending...' : 'Send test push'}
        </button>
      </div>

      {!props.canTestSend && (
        <p className="mt-3 text-sm text-gray-500">Your role is not allowed to send test pushes.</p>
      )}

      {props.isContentTested ? (
        <p className="mt-3 text-sm text-green-700">The current content has been tested.</p>
      ) : (
        <p className="mt-3 text-sm text-orange-700">
          The current content has not been tested yet. Sending is blocked until a test is sent.
        </p>
      )}

      {error != null && (
        <p className="mt-3 border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}

      {result != null && (
        <ul className="mt-3 space-y-1">
          {result.results.map(x => (
            <li key={x.email} className="flex items-center gap-2 text-sm">
              {x.status === 'sent' ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <MinusCircle className="h-4 w-4 text-gray-400" />
              )}
              <span className="text-gray-900">{x.email}</span>
              <span className="text-gray-500">
                {x.status === 'sent'
                  ? 'sent'
                  : `not delivered, ${x.reason == null ? 'no reason given' : REASON_LABELS[x.reason] ?? x.reason}`}
              </span>
            </li>
          ))}
          {result.results.length === 0 && (
            <li className="text-sm text-gray-500">No test addresses returned a result.</li>
          )}
        </ul>
      )}
    </div>
  );
};
